/**
 * ============================================================================
 * useAvailableQuickCreates — Filtra ações do botão "Novo" por permissão
 * ----------------------------------------------------------------------------
 * Mesmo padrão de useAvailableShortcuts: cruza QUICK_CREATE_ACTIONS com as
 * flags de módulo (useHeaderModuleFlags) e as permissões do usuário
 * (usePermissions), retornando somente o que o usuário pode acionar.
 *
 * RELEASE: 2026-10-02 v1.1.0 - Gate dos módulos Veículos, Retenção, Jurídico e Workflow.
 * RELEASE: 2026-07-24 v1.0.0 - Criação.
 * ============================================================================
 */
import { useMemo } from 'react';
import {
  QUICK_CREATE_ACTIONS,
  type QuickCreateDef,
} from '@/config/quickCreate';
import type { ShortcutModule } from '@/config/appShortcuts';
import { useHeaderModuleFlags } from '@/hooks/useHeaderModuleFlags';
import { usePermissions } from '@/hooks/usePermissions';

export function useAvailableQuickCreates(): QuickCreateDef[] {
  const {
    isCrmAvailable,
    isFinancialAvailable,
    isTasksAvailable,
    isReferralAvailable,
    isActivitiesAvailable,
    isKnowledgeAvailable,
    isVehiclesAvailable,
    isRetentionAvailable,
    isLegalAvailable,
    isWorkflowAvailable,
  } = useHeaderModuleFlags();
  const { canView, isAdmin, loading } = usePermissions();

  return useMemo(() => {
    if (loading) return [];

    const moduleEnabled = (m: ShortcutModule): boolean => {
      switch (m) {
        case 'conversations': return true;
        case 'shared':        return true;
        case 'crm':           return isCrmAvailable;
        case 'financial':     return isFinancialAvailable;
        case 'tasks':         return isTasksAvailable;
        case 'referral':      return isReferralAvailable;
        case 'activities':    return isActivitiesAvailable;
        case 'knowledge':     return isKnowledgeAvailable;
        // v1.1.0: módulos novos (Veículos, Retenção, Jurídico, Workflow)
        case 'vehicles':      return isVehiclesAvailable;
        case 'retention':     return isRetentionAvailable;
        case 'legal':         return isLegalAvailable;
        case 'workflow':      return isWorkflowAvailable;
        default:              return false;
      }
    };

    return QUICK_CREATE_ACTIONS.filter((s) => {
      if (!moduleEnabled(s.module)) return false;
      // v1.4.0: rotas admin-only não devem aparecer para não-admin.
      if (s.requireAdmin && !isAdmin) return false;
      if (!s.menuKeys || s.menuKeys.length === 0) return true;
      if (isAdmin) return true;
      return s.menuKeys.some((k) => canView(k));
    });
  }, [
    loading,
    isAdmin,
    canView,
    isCrmAvailable,
    isFinancialAvailable,
    isTasksAvailable,
    isReferralAvailable,
    isActivitiesAvailable,
    isKnowledgeAvailable,
    isVehiclesAvailable,
    isRetentionAvailable,
    isLegalAvailable,
    isWorkflowAvailable,
  ]);
}
